import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/components/Nav";

const HERO_IMG = "/cover.jpg";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.215, 0.61, 0.355, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.15]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden bg-obsidian" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <img src={HERO_IMG} alt="Toyota C-HR de nuit en ville" className="w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-obsidian/30" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 h-full max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 flex flex-col justify-end pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="text-xs uppercase tracking-[0.25em] font-mono text-gold mb-8"
        >
          VTC & Chauffeur privé — Paris · 24/7
        </motion.p>
        <h1 className="font-serif font-light text-cream text-5xl sm:text-7xl lg:text-8xl leading-[1.04] tracking-tight">
          <Line delay={0.35}>L'élégance</Line>
          <Line delay={0.5}>en <span className="italic text-gold">mouvement.</span></Line>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: [0.215, 0.61, 0.355, 1] }}
          className="mt-8 max-w-md text-sm sm:text-base leading-relaxed text-neutral-300"
        >
          Transferts aéroport, mises à disposition, longues distances. Estimation instantanée, tarif fixe confirmé avant le départ.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: [0.215, 0.61, 0.355, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-estimate-cta"
            onClick={() => scrollToId("#reservation")}
            className="group bg-gold text-obsidian text-xs uppercase tracking-[0.2em] font-semibold px-7 py-4 flex items-center gap-2 hover:bg-cream transition-colors duration-300"
          >
            Estimer ma course <ArrowDown size={14} className="group-hover:translate-y-0.5 transition-transform duration-300" />
          </button>
          <Link
            to="/suivi"
            data-testid="hero-tracking-cta"
            className="group border border-cream/40 text-cream text-xs uppercase tracking-[0.2em] px-7 py-4 flex items-center gap-2 hover:border-gold hover:text-gold transition-colors duration-300"
          >
            Suivre ma réservation <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
          </Link>
        </motion.div>
      </motion.div>
    </section>
  );
};
